/* T117 — Project Workspace with 3-stage cards (Sprint 5 / FR-027).
 *
 * Mirrors `New UI Changes/project-workspace.html`. Loads
 * /api/v1/projects/{audit_id} and renders the three stage cards
 * (DD / SPA / Risk) with correct lock propagation. The pipeline
 * strip at the top reflects the highest completed stage.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getProject, rerunProjectStage, restartProjectStage, runRiskAssessment } from '../services/api';
import type { ProjectDetail, StageAssessmentRef, StageRefStatus } from '../types/api';

function statusLabel(status: StageRefStatus): string {
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

function StageCard({
  module,
  num,
  title,
  description,
  assessment,
  onAction,
  onRerun,
  onRestart,
  restartEnabled,
  auditId,
  busy,
}: {
  module: 'dd' | 'spa' | 'risk';
  num: number;
  auditId?: string;
  /* Parent passes true while an action is in flight so the button
     can show a spinner + disable click. Without this the user thinks
     "Start Risk" did nothing when in fact the request is dispatched. */
  busy?: boolean;
  title: string;
  description: string;
  assessment: StageAssessmentRef;
  onAction?: () => void;
  onRerun?: () => void;
  /* Step 3 of restart-from-stage rollout (2026-06-28) — when the
     project response carries restart_from_stage_enabled=true AND the
     stage is in 'failed', show a "Retry from beginning" button that
     hits POST /projects/{auditId}/stage/{stage}/restart. The button
     is hidden by default so a misbehaving FE on a flag-off backend
     can't even attempt the call. */
  onRestart?: () => void;
  restartEnabled?: boolean;
}): JSX.Element {
  /* Sprint 5 client feedback 2026-07-11 (Iter4d) — Stage 3 chip
     label renamed "Risk Module" → "Report Module" so the workspace
     card matches the Draft Report / Stage 3 Report rename elsewhere.
     DD + SPA labels unchanged. */
  const moduleLabel = module === 'dd' ? 'DD Module' : module === 'spa' ? 'SPA Module' : 'Report Module';
  const moduleStr = `Stage 0${num}`;
  const status = assessment.status;
  const locked = status === 'locked';
  const complete = status === 'complete' || status === 'degraded';
  /* Sprint 5 client feedback 2026-06-23 — `status='pending'` is
     overloaded by the BE:
       (a) Fresh project, stage never triggered → assessment.id IS NULL
       (b) Worker queued / running, post-Start click → assessment.id SET
     We only want to treat (b) as "in progress" so the View Progress
     link shows. Case (a) must render the Start button. The earlier
     fix folded both into inProgress=true, which broke newly-created
     projects — they had no assessment.id so no button rendered at
     all (Start hidden because inProgress, View Progress hidden
     because no assessment.id). Discriminator: assessment.id presence. */
  const inProgress =
    status === 'in_progress' ||
    (status === 'pending' && Boolean(assessment.id));
  const failed = status === 'failed';
  /* Sprint 5 client feedback 2026-07-08 (FE Ticket — Cancelled) —
     preserve Cancelled as a distinct terminal state, separate from
     Failed. Cancel is user-initiated and non-error; Failed is
     system-initiated and error. The Result line, the badge, and
     the CSS variant all key off this flag so a manually-cancelled
     stage renders with neutral "Cancelled" styling instead of the
     red Failed pill. */
  const cancelled = status === 'cancelled';

  return (
    <div className={`s5-stage-card ${module} ${locked ? 'locked' : ''}`}>
      {/* Sprint 5 client feedback 2026-06-22 (Second-Set FE Issue 5):
          replace the small italic "Unlocks when prior stage completes"
          footer note with a styled lock badge in the corner per the
          design doc ("Styled lock badge instead of small text label"). */}
      {locked && (
        <span className="s5-lock-badge" aria-label="Locked stage">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Locked
        </span>
      )}
      <div className="stage-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className={`s5-module-chip ${module}`}>{moduleLabel}</span>
        {/* Sprint 5 client feedback 2026-06-19: the worker flips
            status='complete' before the report file is actually
            persisted to MinIO, so the badge said "Complete" while
            opening the report still hit "Report still generating…".
            Show a distinct "Finalising" badge for the gap, so the
            analyst doesn't think the run is fully done yet. Only
            applies to DD/SPA (Risk's artefact is the on-screen page,
            not a separate file). */}
        {(module === 'dd' || module === 'spa') && complete && !assessment.report_ready ? (
          <span className={`s5-status-badge in_progress`} data-tip="Analysis is complete but the report artefact is still being assembled — usually 10-30 sec after worker completion.">
            Finalising report…
          </span>
        ) : (
          <span className={`s5-status-badge ${status}`}>{statusLabel(status)}</span>
        )}
      </div>
      <span className="stage-num">{moduleStr}</span>
      <h3>{title}</h3>
      <p className="stage-sub">{description}</p>
      {/* Sprint 5 client feedback 2026-06-23 (FE Third Doc Issue 2):
          replace the Assessment ID / Started / Completed rows with
          Documents / Result / Report per the design doc — same
          left/right label-value layout, friendlier signals. */}
      <div className="stage-facts">
        <div>
          <span className="k">Documents</span>
          <span>
            {locked
              ? '—'
              : assessment.id
              ? 'Uploaded'
              : 'Pending upload'}
          </span>
        </div>
        <div>
          <span className="k">Result</span>
          <span>
            {locked
              ? 'Locked'
              : complete
              ? 'Completed'
              : inProgress
              ? 'In progress'
              : cancelled
              ? 'Cancelled'
              : failed
              ? 'Failed'
              : 'Not started'}
          </span>
        </div>
        <div>
          <span className="k">Report</span>
          <span>
            {complete && (module === 'dd' || module === 'spa') && !assessment.report_ready
              ? 'Generating…'
              : complete
              ? 'Ready'
              : '—'}
          </span>
        </div>
      </div>
      {/* Step 1 of restart-from-stage rollout (2026-06-28) — when
          the Celery worker writes status='failed' it also persists
          a short failure_reason on the row. Render it under the
          stage facts so the analyst sees WHY (e.g. "QdrantException:
          Connection refused") and not just an opaque red Failed
          badge. Keep it small / muted so it doesn't dominate the
          card layout. */}
      {failed && assessment.failure_reason && (
        <div
          className="stage-failure-reason"
          role="alert"
          style={{
            background: 'rgba(220, 38, 38, 0.08)',
            border: '1px solid rgba(220, 38, 38, 0.25)',
            borderRadius: 6,
            padding: '8px 10px',
            margin: '6px 0 10px',
            fontSize: '0.78rem',
            color: 'var(--fail, #b91c1c)',
            lineHeight: 1.35,
          }}
        >
          <strong style={{ fontWeight: 600 }}>Failure reason:</strong>{' '}
          {assessment.failure_reason}
        </div>
      )}
      <div className="stage-actions">
        {/* DD/SPA — show the View Report button only when the
            backend confirms the report artefact is actually
            available. Sprint-4 worker can flip status='complete'
            before the report row is written; surfacing a button
            that opens an empty report is worse than waiting. */}
        {(module === 'dd' || module === 'spa') && complete && assessment.id && assessment.report_ready && (
          <a
            className="btn btn-primary"
            href={assessment.report_url ?? `/reports/${assessment.id}`}
            /* Sprint 5 client feedback 2026-07-11 (Iter3 SPA #3) —
               explicit hover tooltip so analysts see what the button
               opens without having to click. Text mirrors the button
               label ("Open DD Report" / "Open SPA Report"). */
            data-tip={`Open ${moduleLabel.replace(' Module', '')} Report`}
          >
            View {moduleLabel.replace(' Module', '')} Report
          </a>
        )}
        {(module === 'dd' || module === 'spa') && complete && !assessment.report_ready && (
          <span
            style={{
              fontSize: '0.78rem',
              color: 'var(--muted)',
              fontStyle: 'italic',
            }}
          >
            <span className="spinner spinner-sm" style={{ marginRight: 6 }} />
            Report still generating…
          </span>
        )}
        {/* Risk stage report is a dedicated page. Show the link when
            complete OR degraded so the analyst can always read what's
            there. */}
        {/* Sprint 5 client feedback 2026-06-23 — `complete` already
            includes 'degraded' (see top of StageCard); kept the
            explicit check here for forward-compat. */}
        {module === 'risk' && complete && auditId && (
          <a
            className="btn btn-primary"
            href={`/projects/${auditId}/risk-report`}
            data-tip="Open Draft Report"
          >
            View Draft Report
          </a>
        )}
        {/* Sprint 5 client feedback 2026-06-21: when an
            assessment is in_progress, the previous behaviour
            re-used onAction (which navigates to /upload) and
            mis-led analysts into kicking off a SECOND parallel
            run. Replace the button with a "View Progress" link
            that goes to the live status page for the in-flight
            assessment. DD/SPA → /assessments/<id>;
            Risk → /projects/<auditId>/risk/processing. */}
        {/* Sprint 5 client feedback 2026-06-23 — Risk's processing
            URL uses /projects/<auditId>/risk/processing (not
            /assessments/<id>), so don't gate it on assessment.id;
            even before the worker writes the row, the URL is
            valid. DD/SPA still require assessment.id since their
            URL pattern is /assessments/<id>. */}
        {!locked && !complete && inProgress && (
          (module === 'risk' && auditId) || assessment.id
        ) && (
          <a
            className="btn btn-primary"
            href={
              module === 'risk' && auditId
                ? `/projects/${auditId}/risk/processing`
                : `/assessments/${assessment.id}`
            }
          >
            View Progress
          </a>
        )}
        {!locked && !complete && !inProgress && onAction && (
          <button
            className="btn btn-primary"
            onClick={onAction}
            disabled={busy}
            style={busy ? { opacity: 0.6 } : undefined}
          >
            {busy ? (
              <>
                <span className="spinner spinner-sm" style={{ marginRight: 6 }} />
                Starting…
              </>
            ) : failed ? (
              'Retry'
            ) : (
              `Start ${moduleLabel.replace(' Module', '')}`
            )}
          </button>
        )}
        {/* Client feedback 2026-06-19: "Re-upload / Edit" was showing
            on successfully-completed stages too, which read as a
            retry-style action the analyst didn't need. Restrict to
            FAILED only — a completed DD/SPA/Risk should only offer
            View Report / View Draft Report. */}
        {failed && onRerun && (
          <button className="btn btn-ghost" onClick={onRerun}>
            Re-upload / Edit
          </button>
        )}
        {/* Step 3 of restart-from-stage rollout (2026-06-28) — Retry
            from beginning. Re-uses the existing failed assessment +
            chunks; does NOT require a re-upload. Hidden unless the
            backend feature flag is on (restartEnabled). */}
        {failed && restartEnabled && onRestart && (
          <button
            className="btn btn-primary"
            onClick={onRestart}
            disabled={busy}
            style={busy ? { opacity: 0.6 } : undefined}
            data-tip="Re-run this stage from the beginning using the existing documents."
          >
            {busy ? (
              <>
                <span className="spinner spinner-sm" style={{ marginRight: 6 }} />
                Restarting…
              </>
            ) : (
              'Retry from beginning'
            )}
          </button>
        )}
        {locked && (
          /* The styled <s5-lock-badge> in the corner replaces this
             italic footer note (Sprint 5 Second-Set FE Issue 5); the
             helper sentence stays but at smaller weight so analysts
             still see the unlock condition without the box looking
             text-heavy. */
          <span className="lock-note" style={{ fontSize: '0.72rem', color: 'var(--muted)', textAlign: 'center', marginTop: 'var(--s2)' }}>
            Unlocks when the prior stage completes.
          </span>
        )}
      </div>
    </div>
  );
}

export function ProjectWorkspacePage(): JSX.Element {
  const { auditId } = useParams<{ auditId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!auditId) return;
    void load();

    async function load(): Promise<void> {
      try {
        const p = await getProject(auditId!);
        setProject(p);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load project');
      }
    }
  }, [auditId]);

  /* Sprint 5 — unwrap structured FastAPI error responses so the
     analyst sees the friendly gate-violation message ("Complete
     Stage 2 (SPA) before starting Risk Assessment") instead of
     the bare axios "Request failed with status code 409". */
  function describeError(err: unknown, fallback: string): string {
    const ax = err as {
      response?: { data?: { detail?: unknown } };
      message?: string;
    };
    const detail = ax?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
      const d = detail as { message?: string; required_stage?: string };
      if (d.message) return d.message;
      if (d.required_stage) {
        const label =
          d.required_stage === 'dd' ? 'Stage 1 (DD)' :
          d.required_stage === 'spa' ? 'Stage 2 (SPA)' : d.required_stage;
        return `Complete ${label} before starting this stage.`;
      }
    }
    return ax?.message ?? fallback;
  }

  async function handleRunRisk(): Promise<void> {
    if (!project) return;
    setBusy(true);
    setError(null);
    /* Diagnostic log — analysts have reported the button feeling
       unresponsive. The line below confirms in the browser console
       that the click DID fire. */
    console.info(
      '[Risk] dispatching run for project',
      project.audit_id,
      'SPA status =',
      project.spa_assessment.status,
    );
    try {
      const resp = await runRiskAssessment(project.audit_id);
      console.info('[Risk] backend accepted:', resp);
      /* Sprint 5 client feedback 2026-06-20: Risk had no live progress
         page (unlike DD/SPA which navigate to /processing/<jobId>).
         The dispatched task ran silently and the analyst waited at
         the project workspace with no feedback. Navigate to the new
         RiskProcessingPage which polls /risk every 3 sec, shows
         phase-by-phase progress, and surfaces the View Report CTA
         once the run lands in a terminal state. */
      navigate(`/projects/${project.audit_id}/risk/processing`);
    } catch (err) {
      console.error('[Risk] dispatch failed:', err);
      setError(describeError(err, 'Failed to run Risk Assessment'));
      setBusy(false);
    }
  }

  async function handleRerun(stage: 'dd' | 'spa' | 'risk'): Promise<void> {
    if (!project) return;
    if (!window.confirm(`Re-run ${stage.toUpperCase()}? Downstream stages will be relocked.`)) {
      return;
    }
    setBusy(true);
    try {
      await rerunProjectStage(project.audit_id, stage);
      setProject(await getProject(project.audit_id));
    } catch (err) {
      setError(describeError(err, `Failed to re-run ${stage}`));
    } finally {
      setBusy(false);
    }
  }

  async function handleRestart(stage: 'dd' | 'spa' | 'risk'): Promise<void> {
    if (!project) return;
    const failureReason =
      (stage === 'dd' && project.dd_assessment.failure_reason) ||
      (stage === 'spa' && project.spa_assessment.failure_reason) ||
      (stage === 'risk' && project.risk_assessment.failure_reason) ||
      '(no reason recorded)';
    const ok = window.confirm(
      `Restart ${stage.toUpperCase()} from the beginning?\n\n` +
      `The previous run failed with:\n${failureReason}\n\n` +
      `The same uploaded documents will be re-analysed; nothing is re-uploaded. ` +
      `Downstream stages remain in their current state.`,
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await restartProjectStage(project.audit_id, stage);
      setProject(await getProject(project.audit_id));
    } catch (err) {
      setError(describeError(err, `Failed to restart ${stage}`));
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <div className="page-body">
        <div className="alert alert-fail" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="page-body" style={{ textAlign: 'center', paddingTop: 80 }}>
        <span className="spinner spinner-lg" />
      </div>
    );
  }

  const ddDone = project.dd_assessment.status === 'complete';
  const spaDone = project.spa_assessment.status === 'complete';
  const riskDone = project.risk_assessment.status === 'complete';

  // Sprint 5 client feedback 2026-06-23 (FE Third Doc Issue 2):
  // "Resume Audit" deep-links to whichever stage is next on the
  // pipeline; falls through to /dashboard when all three are done.
  const resumeHref = (() => {
    if (!ddDone) return `/projects/${project.audit_id}/dd/upload`;
    if (!spaDone) return `/projects/${project.audit_id}/spa/upload`;
    if (!riskDone) return `/projects/${project.audit_id}/risk/processing`;
    return null;
  })();
  const auditInProgress = !ddDone || !spaDone || !riskDone;
  const createdDateDisplay = project.created_at
    ? new Date(project.created_at).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      })
    : '—';

  return (
    <div className="page-body">
      {/* Sprint 5 client feedback 2026-06-23 (FE Third Doc Issue 2):
          header redesigned with action buttons on the right,
          status badge below the title, and Analyst/Created metadata
          row. The legacy in-line overall-status pill is replaced
          by the inline "Audit in progress" badge so the call-to-
          action buttons own the right edge. */}
      <div className="page-topbar pwks-topbar">
        <div className="pwks-topbar-left">
          <div className="breadcrumb">
            <a href="/dashboard">Dashboard</a>
            <span className="bc-sep">/</span>
            <a href="/projects">Audit AI</a>
            <span className="bc-sep">/</span>
            <span>{project.project_name}</span>
          </div>
          <h1 className="pwks-title">{project.project_name}</h1>
          {auditInProgress ? (
            <span className="pwks-status-pill pwks-status-pill-progress">
              <span className="pwks-status-dot" />
              Audit in progress
            </span>
          ) : (
            <span className="pwks-status-pill pwks-status-pill-done">
              <span className="pwks-status-dot" />
              Audit complete
            </span>
          )}
          <div className="pwks-meta-row">
            <span><span className="k">Analyst</span> {project.analyst_name ?? '—'}</span>
            <span className="dot-sep">·</span>
            <span><span className="k">Created</span> {createdDateDisplay}</span>
            <span className="dot-sep">·</span>
            <span className="t-mono">{project.audit_id}</span>
            <span className="dot-sep">·</span>
            <span>{project.target_entity}</span>
            {project.investment_type && (
              <>
                <span className="dot-sep">·</span>
                <span>{project.investment_type}</span>
              </>
            )}
            {project.jurisdiction && (
              <>
                <span className="dot-sep">·</span>
                <span>{project.jurisdiction}</span>
              </>
            )}
          </div>
        </div>
        <div className="pwks-topbar-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/projects')}
          >
            All Reports
          </button>
          {resumeHref ? (
            <button
              type="button"
              className="btn btn-accent"
              onClick={() => navigate(resumeHref)}
            >
              Resume Audit
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-accent"
              onClick={() => navigate(`/projects/${project.audit_id}/risk-report`)}
              data-tip="Open Draft Report"
            >
              View Draft Report
            </button>
          )}
        </div>
      </div>

      {/* Sprint 5 client feedback 2026-06-23 (FE Third Doc Issue 2):
          tightened pipeline strip padding (var(--s5)/(--s6) →
          var(--s3)/(--s5)) per the doc's "Reduce excessive
          height/padding" ask. Connected line + green-completed /
          orange-active / grey-upcoming states already live in the
          existing .s5-pipeline-step CSS. */}
      <div className="s5-pipeline-strip pwks-pipeline-strip">
        <span className={`s5-pipeline-step ${ddDone ? 'done' : 'active'}`}>
          <span className="num">{ddDone ? '✓' : '1'}</span> Due Diligence
        </span>
        <span className={`s5-pipeline-line ${ddDone ? 'done' : ''}`} />
        <span className={`s5-pipeline-step ${spaDone ? 'done' : ddDone ? 'active' : ''}`}>
          <span className="num">{spaDone ? '✓' : '2'}</span> SPA Legal Review
        </span>
        <span className={`s5-pipeline-line ${spaDone ? 'done' : ''}`} />
        <span className={`s5-pipeline-step ${riskDone ? 'done' : spaDone ? 'active' : ''}`}>
          <span className="num">{riskDone ? '✓' : '3'}</span> Risk Assessment
        </span>
      </div>

      <div className="s5-stage-grid">
        <StageCard
          module="dd"
          num={1}
          title="Due Diligence Assessment"
          description="Investment memo, financial DD, techno-commercial DD & IC resolution — metrics extracted, calculations verified, benchmarked."
          assessment={project.dd_assessment}
          onAction={() => navigate(`/projects/${project.audit_id}/dd/upload`)}
          onRerun={() => handleRerun('dd')}
          onRestart={() => handleRestart('dd')}
          restartEnabled={Boolean(project.restart_from_stage_enabled)}
          auditId={project.audit_id}
          busy={busy}
        />
        <StageCard
          module="spa"
          num={2}
          title="SPA Legal Review"
          description="Clause detection, investor-protection analysis & legal issue classification."
          assessment={project.spa_assessment}
          onAction={() => navigate(`/projects/${project.audit_id}/spa/upload`)}
          onRerun={() => handleRerun('spa')}
          onRestart={() => handleRestart('spa')}
          restartEnabled={Boolean(project.restart_from_stage_enabled)}
          auditId={project.audit_id}
          busy={busy}
        />
        <StageCard
          module="risk"
          num={3}
          title="Draft Report"
          description="Consolidates DD + SPA outputs into one unified risk verdict & recommendation."
          assessment={project.risk_assessment}
          onAction={spaDone ? handleRunRisk : undefined}
          onRerun={() => handleRerun('risk')}
          onRestart={() => handleRestart('risk')}
          restartEnabled={Boolean(project.restart_from_stage_enabled)}
          auditId={project.audit_id}
          busy={busy}
        />
      </div>

      {busy && (
        <p style={{ textAlign: 'center', color: 'var(--muted)', marginTop: 'var(--s4)' }}>
          Working…
        </p>
      )}

      {/* Sprint 5 client feedback 2026-06-24 — "Audit trail enabled"
          informational panel removed from the project workspace.
          The panel + its "Configure trigger" CTA added noise without
          functional payoff: the audit log is always-on regardless of
          this panel's presence, and Configure trigger routed back to
          /dashboard (no dedicated settings page exists). Panel CSS
          (.pwks-info-panel et al.) intentionally kept in index.css so
          a future settings-route reintroduction doesn't need to
          re-style from scratch. */}
    </div>
  );
}
